// Colour themes for the admin dashboard.
//
// `brand` is the SolushipX look (black, white, blue #2563EB, system font) and is
// what the admin dashboard renders in (App.js AdminDashboardRoute). `classic` is
// the older indigo/slate palette; it is only the fallback for dashboard
// components rendered outside a DashboardThemeProvider.
//
// Styles reach the palette two ways:
//   - CSS (the stylesheet and MUI `sx` strings) reads var(--dash-*, <classic hex>),
//     so nothing changes unless DashboardThemeProvider sets the variables. They are
//     set on <html> so portalled drawers and menus pick them up too.
//   - JS that needs a real colour (Chart.js, SVG attributes, hex-alpha strings)
//     reads useDashboardTheme().

import React, { createContext, useContext, useLayoutEffect, useMemo } from 'react';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';

const SYSTEM_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

export const DASHBOARD_PALETTES = {
    classic: {
        accent: '#6366f1',
        accentDark: '#4f46e5',
        accentSoft: '#eef2ff',
        accentRgb: '99, 102, 241',
        info: '#0ea5e9',
        ink: '#0f172a',
        body: '#475569',
        muted: '#64748b',
        faint: '#94a3b8',
        surface: '#f8fafc',
        subtle: '#f1f5f9',
        line: '#e2e8f0',
        line2: '#cbd5e1',
        carrierBars: ['#6366f1', '#10b981', '#0ea5e9', '#eab308', '#ec4899', '#8b5cf6', '#06b6d4', '#84cc16'],
        font: null
    },
    brand: {
        accent: '#2563eb',
        accentDark: '#1d4ed8',
        accentSoft: '#eff6ff',
        accentRgb: '37, 99, 235',
        info: '#3b82f6',
        ink: '#000000',
        body: '#374151',
        muted: '#6b7280',
        faint: '#9ca3af',
        surface: '#f8f9fa',
        subtle: '#f3f4f6',
        line: '#e5e7eb',
        line2: '#d1d5db',
        carrierBars: ['#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#1d4ed8', '#111827', '#6b7280', '#9ca3af'],
        font: SYSTEM_FONT
    }
};

const CSS_VARS = {
    accent: '--dash-accent',
    accentDark: '--dash-accent-dark',
    accentSoft: '--dash-accent-soft',
    accentRgb: '--dash-accent-rgb',
    info: '--dash-info',
    ink: '--dash-ink',
    body: '--dash-body',
    muted: '--dash-muted',
    faint: '--dash-faint',
    surface: '--dash-surface',
    subtle: '--dash-subtle',
    line: '--dash-line',
    line2: '--dash-line-2',
    font: '--dash-font'
};

const DashboardThemeContext = createContext(DASHBOARD_PALETTES.classic);

export const useDashboardTheme = () => useContext(DashboardThemeContext);

export const DashboardThemeProvider = ({ palette = 'classic', children }) => {
    const colors = DASHBOARD_PALETTES[palette] || DASHBOARD_PALETTES.classic;
    const outer = useTheme();
    // Built fresh rather than merged into the app theme: MUI bakes the font into
    // every typography variant at creation, so overriding fontFamily on an
    // existing theme leaves the variants on Roboto. The app theme only customises
    // `warning`, which is carried over.
    const muiTheme = useMemo(() => (colors.font
        ? createTheme({
            palette: {
                warning: outer.palette.warning,
                primary: { main: colors.accent, dark: colors.accentDark, contrastText: '#ffffff' }
            },
            typography: { fontFamily: colors.font }
        })
        : outer), [outer, colors]);

    useLayoutEffect(() => {
        if (colors === DASHBOARD_PALETTES.classic) return undefined;
        const root = document.documentElement;
        Object.entries(CSS_VARS).forEach(([key, name]) => {
            if (colors[key]) root.style.setProperty(name, colors[key]);
        });
        root.classList.add('dash-theme-brand');
        return () => {
            Object.values(CSS_VARS).forEach((name) => root.style.removeProperty(name));
            root.classList.remove('dash-theme-brand');
        };
    }, [colors]);

    return (
        <DashboardThemeContext.Provider value={colors}>
            <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
        </DashboardThemeContext.Provider>
    );
};
